import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { 
  LayoutDashboard, 
  Database, 
  PlusCircle, 
  UploadCloud, 
  PlayCircle, 
  History, 
  FileWarning, 
  BookA, 
  Layers, 
  Settings,
  MessageSquareWarning,
  UserCircle2,
  Menu,
  X,
  Shield,
  BookOpen,
  Library,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { testStateService } from "@/services/testStateService";

const menuItems = [
  { group: "MAIN", items: [
    { title: "Dashboard", path: "/dashboard", icon: LayoutDashboard, translationKey: "dashboard" },
    { title: "Painel Admin", path: "/admin", icon: Shield, adminOnly: true, translationKey: "adminPanel" },
    { title: "Banco de Questões", path: "/question-bank", icon: Database, adminOnly: true, translationKey: "questions" },
    { title: "Nova Questão", path: "/new-question", icon: PlusCircle, adminOnly: true, translationKey: "newQuestion" },
    { title: "Importar Questões", path: "/questions/import", icon: UploadCloud, adminOnly: true, translationKey: "importQuestions" },
    { title: "Conteúdos de Gramática", path: "/admin/grammar", icon: BookOpen, adminOnly: true, translationKey: "adminGrammar" },
    { title: "Importar Exercícios", path: "/admin/exercises", icon: UploadCloud, adminOnly: true, translationKey: "adminExercises" },
    { title: "Iniciar Teste", path: "/start-test", icon: PlayCircle, translationKey: "startTest" },
  ]},
  { group: "ESTUDO", items: [
    { title: "Histórico", path: "/history", icon: History, translationKey: "history" },
    { title: "Gramática", path: "/grammar", icon: BookOpen, translationKey: "grammar" },
    { title: "Coin Lecture", path: "/reading", icon: Library, translationKey: "reading" },
    { title: "Banque d'Exercices", path: "/exercises", icon: Library, translationKey: "exercises" },
    { title: "Banco de Erros", path: "/writing-errors", icon: FileWarning, translationKey: "writingErrors" },
    { title: "Banco de Palavras", path: "/vocabulary", icon: BookA, translationKey: "vocabulary" },
    { title: "Flashcards", path: "/flashcards", icon: Layers, translationKey: "flashcards" },
  ]},
  { group: "SISTEMA", items: [
    { title: "Configurações", path: "/settings", icon: Settings, translationKey: "settings" },
  ]}
];

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  console.log("[LANGUAGE ACTIVE]", currentLanguage);
  console.log("[I18N_FR_CHECK]", {
    language: i18n.language,
    dashboardTitle: t("dashboard.title"),
    sidebarStartTest: t("sidebar.startTest"),
    flashcardsTitle: t("flashcards.title")
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuCollapsed, setIsDesktopMenuCollapsed] = useState(() => {
    return localStorage.getItem("tef_sidebar_collapsed") === "true";
  });
  
  const toggleDesktopMenu = () => {
    const newState = !isDesktopMenuCollapsed;
    setIsDesktopMenuCollapsed(newState);
    localStorage.setItem("tef_sidebar_collapsed", String(newState));
  };

  const { user, signOut, isAdmin, adminLoading } = useAuth();

  const userEmail = user?.email?.trim().toLowerCase();
  const isEmergencyAdmin = userEmail === "luciogiorpicanco@gmail.com" || userEmail === "lucioigorpicanco@gmail.com";
  const hasAdminAccess = isEmergencyAdmin || isAdmin;

  function handleNavigate(path: string) {
    console.log("[SIDEBAR_NAVIGATE]", {
      from: window.location.pathname,
      to: path,
    });
    navigate(path);
  }

  useEffect(() => {
    console.log("[APP_RENDER_ROUTE]", {
      pathname: location.pathname,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes("/test")) {
      testStateService.setTestInProgress(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (err) {
      console.error("Erro ao deslogar:", err);
    }
  };

  return (
    <div className="flex min-h-dvh w-full overflow-x-hidden bg-[#F3F4F6]">
      
      {/* Mobile Drawer Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 md:hidden animate-in fade-in duration-200" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "bg-white border-r border-gray-200 flex flex-col justify-between flex-shrink-0 z-50 shadow-sm transition-all duration-300 ease-in-out md:fixed md:inset-y-0 md:left-0 md:translate-x-0 fixed inset-y-0 left-0 h-full md:h-dvh",
        isMobileMenuOpen ? "translate-x-0 shadow-2xl w-[280px]" : "-translate-x-full md:translate-x-0",
        isDesktopMenuCollapsed ? "md:w-[80px]" : "md:w-[280px]"
      )}>
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Logo Area */}
          <div className={cn(
            "p-6 flex items-center justify-between gap-3 border-b border-gray-100/50 transition-all duration-300",
            isDesktopMenuCollapsed ? "justify-center p-4" : ""
          )}>
            <div className={cn("flex items-center gap-3 min-w-0 transition-all duration-300", isDesktopMenuCollapsed ? "flex-col gap-1" : "")}>
              <img src="/apple-touch-icon.png" alt="TEF Canada Trainer" className={cn("rounded-xl object-contain shrink-0 shadow-xs transition-all duration-300", isDesktopMenuCollapsed ? "w-8 h-8" : "w-10 h-10")} />
              {!isDesktopMenuCollapsed && (
                <div className="flex items-center gap-2 truncate">
                  <span className="font-extrabold text-red-600 truncate text-base tracking-tight">TEF Canada</span>
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-red-600 shrink-0 border border-red-100 shadow-2xs">Premium</span>
                </div>
              )}
            </div>
            {!isDesktopMenuCollapsed && (
              <button 
                type="button" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="md:hidden p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-6 overflow-y-auto py-2">
            {menuItems.map((group, idx) => {
              // Hide the entire group if all its items are administrative and the user is not an admin
              const visibleItems = group.items.filter(item => {
                if (!item.adminOnly) return true;
                if (adminLoading && !isEmergencyAdmin) return true; // Show until we are sure
                return hasAdminAccess;
              });
              if (visibleItems.length === 0) return null;

              return (
                <div key={idx} className={cn("transition-all duration-300", isDesktopMenuCollapsed ? "px-1" : "")}>
                  {!isDesktopMenuCollapsed ? (
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
                      {t(`sidebar.groups.${group.group}`, group.group)}
                    </h3>
                  ) : (
                    <div className="h-4 border-b border-gray-100/50 mb-3 mx-2" />
                  )}
                  <ul className="space-y-1">
                    {visibleItems.map((item) => {
                      const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
                      return (
                        <li key={item.path}>
                          <a
                            href={item.path}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsMobileMenuOpen(false);
                              handleNavigate(item.path);
                            }}
                            title={isDesktopMenuCollapsed ? t(`sidebar.${item.translationKey}`, item.title) : undefined}
                            className={cn(
                              "flex items-center gap-3 rounded-xl transition-all duration-200 font-medium cursor-pointer",
                              isDesktopMenuCollapsed ? "justify-center p-3 mx-1" : "px-3 py-2.5 text-[15px]",
                              isActive 
                                ? "bg-primary/10 text-primary" 
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            )}
                          >
                            <item.icon className={cn("shrink-0 transition-all duration-300", isDesktopMenuCollapsed ? "w-6 h-6" : "w-5 h-5", isActive ? "text-primary" : "text-gray-500")} />
                            {!isDesktopMenuCollapsed && (
                              <span className="truncate">{t(`sidebar.${item.translationKey}`, item.title)}</span>
                            )}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </nav>
        </div>

        {/* Footer User Area */}
        <div className={cn("p-4 border-t border-gray-100 flex flex-col gap-3 relative", isDesktopMenuCollapsed ? "items-center px-2" : "")}>
          <button 
            onClick={toggleDesktopMenu}
            className="absolute -top-4 -right-4 hidden md:flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-full shadow-sm text-gray-500 hover:text-primary hover:border-primary transition-colors z-50"
            title={isDesktopMenuCollapsed ? "Expandir menu" : "Recolher menu"}
          >
            {isDesktopMenuCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          {!isDesktopMenuCollapsed && <LanguageSwitcher />}
          
          <button className={cn(
            "flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors",
            isDesktopMenuCollapsed ? "justify-center p-2 rounded-xl hover:bg-gray-50" : "w-full px-3 py-2"
          )}
          title={isDesktopMenuCollapsed ? t("sidebar.feedback", "Enviar feedback") : undefined}>
            <MessageSquareWarning className={cn("shrink-0", isDesktopMenuCollapsed ? "w-5 h-5" : "w-4 h-4")} />
            {!isDesktopMenuCollapsed && t("sidebar.feedback", "Enviar feedback")}
          </button>
          
          <div className={cn(
            "flex items-center gap-3 border border-gray-100 bg-white shadow-sm transition-all duration-300",
            isDesktopMenuCollapsed ? "flex-col p-2 rounded-xl justify-center w-full" : "p-3 rounded-xl"
          )}>
            <UserCircle2 className={cn("text-gray-400 flex-shrink-0 transition-all duration-300", isDesktopMenuCollapsed ? "w-8 h-8" : "w-10 h-10")} />
            
            {!isDesktopMenuCollapsed && (
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {user?.email || t("sidebar.student", "Estudante TEF")}
                </span>
                <span className="text-xs text-gray-500 truncate">
                  {user ? t("sidebar.connectedAccount", "Conta conectada") : t("sidebar.premiumAccount", "Conta Premium")}
                </span>
              </div>
            )}
            
            {user && (
              <button
                onClick={handleSignOut}
                className={cn(
                  "hover:bg-gray-100 rounded-lg text-gray-400 hover:text-red-500 transition-colors flex items-center justify-center",
                  isDesktopMenuCollapsed ? "w-full p-2 mt-1" : "p-1.5"
                )}
                title={t("common.logout", "Sair")}
              >
                <LogOut className={cn(isDesktopMenuCollapsed ? "w-5 h-5" : "w-4 h-4")} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area Wrapper */}
      <div className={cn(
        "flex min-h-dvh min-w-0 flex-1 flex-col w-full transition-all duration-300 ease-in-out",
        isDesktopMenuCollapsed ? "md:pl-[80px]" : "md:pl-[280px]"
      )}>
        {/* Mobile Header Bar */}
        <div className="flex md:hidden items-center justify-between px-4 py-3 bg-white border-b border-gray-200 w-full shrink-0 z-20 shadow-sm sticky top-0">
          <button 
            type="button"
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors focus:outline-none flex items-center justify-center"
            aria-label="Abrir menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 min-w-0 justify-center flex-1 mx-2">
            <img src="/apple-touch-icon.png" alt="TEF Canada Trainer" className="w-8 h-8 rounded-lg object-contain shrink-0" />
            <div className="flex items-center gap-1.5 truncate">
              <span className="font-bold text-red-600 truncate text-sm">TEF Canada</span>
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-600 shrink-0 border border-red-100">Premium</span>
            </div>
          </div>
          <div className="w-9 h-9 shrink-0" />
        </div>

        <main className="min-w-0 flex-1 overflow-x-hidden relative">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-in fade-in duration-500">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
